import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { parse as parseQuery } from 'query-string'
import { useSearchParams } from 'react-router-dom'
import { useConfig } from 'builder/hooks/useConfig'
import { DocumentsPanel, FeaturesPanel } from 'builder/components/Panel'
import { useConstant } from 'builder/hooks/useConstant'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import {
  ResumeReviewScenarios,
  actions as resumeReviewActions,
  selectors as resumeReviewSelectors,
} from 'builder/modules/resumeReview'
import {
  selectors as dashboardSelectors,
  actions as dashboardActions,
} from 'builder/modules/dashboard'
import { useDocumentQueryHandler } from 'builder/components/Panel/hooks'
import TopResumeNotification from 'builder/components/Panel/TopResumeNotification'
import { PanelProps } from 'builder/components/Panel/types'
import { BackToOverviewDialog } from 'builder/components/AutoTailoring/BackToOverviewDialog'
import { useAutoTailoringAppConfig } from 'builder/hooks/useAutoTailoringAppConfig'
import { TabletAndDesktopVirtualCoach, useVirtualCoachFeature } from 'builder/features/virtualCoach'
import { UpgradeModal } from 'builder/components/UpgradeModal/UpgradeModal'
import { actions as uiActions } from 'builder/modules/ui'
import { PanelSessionStorageKeys } from 'builder/modules/panel'

type DashboardViewProps = {
  isDocumentsPage?: boolean
} & PanelProps

export const Dashboard = ({ isDocumentsPage, ...props }: DashboardViewProps) => {
  const dispatch = useDispatch()
  const config = useConfig()
  const resumeReviewScenario = useTypedSelector(resumeReviewSelectors.scenario)
  const { isAutoTailoringEnabled } = useAutoTailoringAppConfig()
  const { isVirtualCoachEnabled } = useVirtualCoachFeature()
  // Find out which resume the user edited before being paid
  const downloadDocumentId = useConstant(() => {
    const params = parseQuery(window.location.search, { parseNumbers: true })
    return params.download_document_id
  })
  const showUpgradeModal = useTypedSelector(dashboardSelectors.showUpgradeModal)
  const [searchParams, setSearchParams] = useSearchParams()

  const hasUserComeFromPayment = typeof downloadDocumentId === 'number'

  // Some of our websites send user resumes to TopResume and we have to notify people about emails
  const shouldWarnUserAboutEmail =
    hasUserComeFromPayment && resumeReviewScenario === ResumeReviewScenarios.external

  useEffect(() => {
    const paymentModal = searchParams.get('payment_modal')
    if (paymentModal) {
      // eslint-disable-next-line
      const { payment_modal, ...params } = searchParams as { payment_modal?: string }
      setSearchParams(params)
      dispatch(dashboardActions.setShowUpgradeModal(true))
    }
  }, [searchParams, dispatch, setSearchParams])

  useEffect(() => {
    if (resumeReviewScenario === ResumeReviewScenarios.embedded) {
      dispatch(resumeReviewActions.fetchCritiques())
    }
  }, [dispatch, resumeReviewScenario])

  // Download a file if it's defined in the query params and sends it to topresume
  useDocumentQueryHandler()

  const handleUpgradeModalClose = () => {
    sessionStorage.removeItem(PanelSessionStorageKeys.ACTION_AFTER_UPGRADING_PLAN)
    dispatch(uiActions.closeLimitModal())
    dispatch(dashboardActions.setShowUpgradeModal(false))
  }
  const showDesktopType = () => {
    if (isDocumentsPage) return <DocumentsPanel {...props} />
    if (
      config?.features.superApp ||
      config?.features.international ||
      config?.features.dashboardV2 ||
      config?.features.dashboardV3
    ) {
      return <FeaturesPanel {...props} />
    }
    return <DocumentsPanel {...props} />
  }

  return (
    <>
      {/* Notify about upcoming email from TopResume */}
      {shouldWarnUserAboutEmail && <TopResumeNotification />}
      {showDesktopType()}
      {isAutoTailoringEnabled && <BackToOverviewDialog />}
      {isVirtualCoachEnabled && (
        <TabletAndDesktopVirtualCoach layoutVariant="default" triggerVariant="default" />
      )}
      {showUpgradeModal && (
        <UpgradeModal
          onPaymentSuccess={(piToken: string) => {
            handleUpgradeModalClose()
            window.location.href = `/app/billing/processing?pi_token=${piToken}`
          }}
          onClose={handleUpgradeModalClose}
        />
      )}
    </>
  )
}
